import React, { useState } from 'react';
import { Music, Upload, X, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { SUPPORTED_LANGUAGES } from '../types';

interface AudioStepUploadProps {
  audioUrls: Record<string, string>;
  onUpdate: (audioUrls: Record<string, string>) => void;
  disabled?: boolean;
}

const AudioStepUpload: React.FC<AudioStepUploadProps> = ({
  audioUrls,
  onUpdate,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const allowedExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];
  const maxSize = 50 * 1024 * 1024; // 50MB

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return `File size must be less than ${maxSize / (1024 * 1024)}MB`;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedExtensions.includes(extension)) {
      return `Only ${allowedExtensions.join(', ')} files are allowed`;
    }

    return null;
  };

  const handleFileUpload = async (file: File, language: string) => {
    setError('');
    setUploading(language);

    try {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      const uploadResult = await apiService.uploadAudio(file);
      if (uploadResult.success) {
        onUpdate({
          ...audioUrls,
          [language]: uploadResult.data.url,
        });
      } else {
        setError('Upload failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, language: string) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, language);
    }
  };

  const handleDelete = (language: string) => {
    const url = audioUrls[language];
    if (!url) return;

    // Set the URL to empty string instead of deleting the key
    const newAudioUrls = { ...audioUrls, [language]: '' };
    onUpdate(newAudioUrls);
    setError(''); // Clear any previous errors
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Music className="h-5 w-5 text-gray-500" />
        <h4 className="text-sm font-medium text-gray-700">Audio Files</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(SUPPORTED_LANGUAGES).map(([langCode, langName]) => {
          const currentUrl = audioUrls[langCode];
          const isUploading = uploading === langCode;

          return (
            <div key={langCode} className="space-y-2">
              <label className="block text-xs font-medium text-gray-600">
                {langName}
              </label>

              {currentUrl ? (
                // Show current file
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Music className="h-4 w-4 text-gray-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        Audio uploaded
                      </p>
                      <p className="text-xs text-gray-500 truncate max-w-[120px]" title={currentUrl.split('/').pop()}>
                        {currentUrl.split('/').pop()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <a
                      href={currentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-500 text-xs"
                    >
                      Play
                    </a>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(langCode);
                      }}
                      disabled={disabled}
                      className="text-red-600 hover:text-red-500 disabled:opacity-50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ) : (
                // Upload area
                <div className="relative">
                  <input
                    type="file"
                    accept={allowedExtensions.map(ext => `.${ext}`).join(',')}
                    onChange={(e) => handleFileSelect(e, langCode)}
                    className="hidden"
                    id={`audio-upload-${langCode}`}
                    disabled={disabled}
                  />
                  <label
                    htmlFor={`audio-upload-${langCode}`}
                    className={`block p-3 border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
                      isUploading
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-300 hover:border-gray-400'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isUploading ? (
                      <div className="space-y-1">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="text-xs text-gray-600">Uploading...</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Upload className="h-4 w-4 text-gray-400 mx-auto" />
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-indigo-600">Click to upload</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {allowedExtensions.join(', ').toUpperCase()} up to {formatFileSize(maxSize)}
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default AudioStepUpload;
